// Affiliates — public program page. Offer: flat 30% on everything, 90-day window.
// Program runs on Rewardful; SIGNUP_URL is the Rewardful-hosted signup for our campaign.
import { CRWIcon, type IconName } from '../components/icons';
import { MetalBtn } from '../components/primitives';
import { DISCORD_INVITE, SUPPORT_EMAIL } from '../sections/footer';
import { Callout, DocSection, MetaChip, PageShell, usePageTitle } from './shell';

// TODO(rewardful-go-live): replace with the real Rewardful signup URL once the
// account exists (Dashboard → Campaign → Affiliate signup link).
const SIGNUP_URL = 'https://clipradar.getrewardful.com/signup';

const NUMBERS: [string, string][] = [
  ['30%', 'commission on every sale — subscriptions AND lifetime licenses'],
  ['90', 'day referral window — your link keeps credit for three months'],
  ['recurring', 'subscriptions pay you every month, for as long as they stay'],
  ['$0', 'to join — free license for creators who make content about editing'],
];

export function AffiliatesPage() {
  usePageTitle('Affiliate program');
  const steps: { icon: IconName; title: string; body: string }[] = [
    {
      icon: 'tag', title: '1 · Get your link',
      body: 'Sign up in two minutes. You get a personal link — and if you make videos, a coupon code (like YOURNAME10) that gives your audience 10% off and tracks sales even when nobody clicks anything.',
    },
    {
      icon: 'play', title: '2 · Show it working',
      body: 'A 30-second clip of ClipRadar finding a shot is the whole pitch. We hand you a full assets pack — demo clips, screenshots, talking points — and a free license so you can show YOUR footage, not ours.',
    },
    {
      icon: 'zap', title: '3 · Get paid monthly',
      body: 'Flat 30% on everything: $29/mo and $249/yr subscriptions pay recurring for the life of the customer; the $199 lifetime license pays ~$60 per sale. Payouts go out monthly once a commission clears the 30-day approval window.',
    },
  ];
  return (
    <PageShell
      kicker="Affiliates"
      title={<>Earn 30% recommending the tool editors already want.</>}
      sub="ClipRadar searches footage by what's on screen, what's said, and who's in frame — 100% locally. If your audience edits video, this is the easiest demo you'll ever record."
      meta={<><MetaChip>Flat 30% · everything</MetaChip><MetaChip>90-day window</MetaChip><MetaChip>Recurring on subscriptions</MetaChip></>}
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, margin: '4px 0 30px' }}>
        {NUMBERS.map(([big, small]) => (
          <div key={small} style={{ padding: '18px 16px', borderRadius: 12, border: '1px solid var(--line-1)', background: 'var(--surface-card)' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, letterSpacing: 'var(--tracking-display)', color: 'var(--signal-400)' }}>{big}</div>
            <div style={{ marginTop: 4, fontSize: 12.5, lineHeight: 1.5, color: 'var(--fg-3)' }}>{small}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14, margin: '0 0 8px' }}>
        {steps.map((s) => (
          <div key={s.title} style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '22px 20px', borderRadius: 14, border: '1px solid var(--line-2)', background: 'var(--surface-card)' }}>
            <span style={{ display: 'grid', placeItems: 'center', width: 36, height: 36, borderRadius: 10, background: 'var(--signal-dim)', border: '1px solid rgba(74,227,255,0.25)', color: 'var(--signal-400)' }}>
              <CRWIcon d={s.icon} size={16} />
            </span>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, letterSpacing: 'var(--tracking-heading)', color: 'var(--fg-1)' }}>{s.title}</span>
            <span style={{ fontSize: 13.5, lineHeight: 1.65, color: 'var(--fg-2)' }}>{s.body}</span>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 14, padding: '24px 24px', margin: '22px 0 6px', borderRadius: 14, border: '1px solid var(--line-2)', background: 'var(--surface-card)' }}>
        <div style={{ flex: '1 1 320px' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600, letterSpacing: 'var(--tracking-heading)', color: 'var(--fg-1)' }}>Ready when you are.</div>
          <p style={{ margin: '6px 0 0', fontSize: 13.5, lineHeight: 1.6, color: 'var(--fg-2)', maxWidth: 520 }}>
            Signup takes two minutes and approval usually happens the same day. Make videos about editing?
            Email <a href={`mailto:${SUPPORT_EMAIL}`} className="crw-textlink" style={{ color: 'var(--signal-400)' }}>{SUPPORT_EMAIL}</a> after
            you sign up and we'll send a free lifetime license + the assets pack.
          </p>
        </div>
        <a href={SIGNUP_URL} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
          <MetalBtn>Become an affiliate →</MetalBtn>
        </a>
      </div>

      <DocSection n="01" title="How tracking works" id="tracking">
        <p>
          Your link drops a 90-day cookie on getclipradar.com — any purchase inside that window is yours, including
          someone who takes the free trial first and buys three weeks later. Your coupon code works even without a
          click: when a customer enters it at checkout, the sale is credited to you. You get a live dashboard with
          clicks, trials, conversions, and commissions.
        </p>
      </DocSection>

      <DocSection n="02" title="Payouts" id="payouts">
        <p>
          Commissions are approved 30 days after the sale (that covers our 14-day refund policy with margin), then
          paid out monthly. Payouts and payment details are handled through our program dashboard — you'll set up
          how you want to be paid when you sign up. Subscriptions keep paying you 30% every renewal for as long as
          the customer stays.
        </p>
      </DocSection>

      <DocSection n="03" title="Program rules" id="rules">
        <p style={{ marginBottom: 10 }}>Short version — don't do anything you'd be embarrassed to explain:</p>
        <ul style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13.5, lineHeight: 1.65, color: 'var(--fg-2)' }}>
          <li>No self-referrals — buying through your own link won't be credited.</li>
          <li>No bidding on "ClipRadar" or misspellings in search ads, and no posing as ClipRadar itself.</li>
          <li>No posting codes to coupon/deal aggregator sites — codes are for YOUR audience.</li>
          <li>Disclose the affiliate relationship where the law requires it (FTC and equivalents).</li>
          <li>Keep claims honest: ClipRadar requires Windows and an NVIDIA GPU, and runs 100% locally — say so.</li>
        </ul>
        <Callout kind="tip">
          Rates only ever change for FUTURE sales with 30 days' notice — commissions you've already earned are never
          touched retroactively.
        </Callout>
      </DocSection>

      <DocSection n="04" title="Questions" id="faq">
        <p>
          Anything unclear — payout timing, content ideas, whether your channel qualifies for a free license — email{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="crw-textlink" style={{ color: 'var(--signal-400)' }}>{SUPPORT_EMAIL}</a> or ask in the{' '}
          <a href={DISCORD_INVITE} target="_blank" rel="noopener noreferrer" className="crw-textlink" style={{ color: 'var(--signal-400)' }}>Discord</a>.
          You're talking to the founder either way.
        </p>
      </DocSection>
    </PageShell>
  );
}
